import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Loader2, MessageSquare, Trash2, Check, CheckCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/cn";

// Relative-time formatter (no external dep) — "just now", "5m", "3h", "2d".
function fmtRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 45) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  return `${d}d`;
}

// Delete control for one message. Kept visible on focus as well as hover, so it
// is reachable by keyboard and not only by mouse — an icon that only exists on
// :hover is an action a keyboard user cannot find at all.
function DeleteButton({ m, busy, onClick }) {
  const when = fmtRelative(m.createdAt);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      title="Delete this message"
      aria-label={`Delete message sent ${when || "earlier"}`}
      className="grid h-6 w-6 shrink-0 place-items-center rounded-lg text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400/50 disabled:cursor-not-allowed group-hover:opacity-100 dark:hover:bg-rose-500/10"
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
    </button>
  );
}

/**
 * Reusable support-chat panel. The widget mounts it with role="user"; the
 * SuperAdmin inbox mounts it with role="admin". The only difference is the API
 * surface handed in and the role stamped on outgoing messages.
 *
 * Props:
 *   ticket           : { id, status, ... }
 *   role             : 'admin' | 'user'
 *   api              : { listMessages(id), postMessage(id, body), deleteMessage?(id, msgId) }
 *   locked           : boolean — disable the composer (ticket closed)
 *   pollMs           : number — poll cadence (default 7000)
 *   onTicketUpdated  : optional callback after each successful send
 *   heightClass      : tailwind height class for the scroll area
 */
export default function SupportChat({
  ticket,
  role,
  api,
  locked,
  pollMs = 7000,
  onTicketUpdated,
  heightClass = "h-[clamp(220px,40vh,360px)]",
}) {
  const { user } = useAuth();
  const meId = user?.id;
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The message currently being deleted, so only that bubble's control spins —
  // and so a second click can't fire the same DELETE twice.
  const [deletingId, setDeletingId] = useState(null);
  const scrollRef = useRef(null);

  // Who may delete WHAT — deliberately the same rule the backend enforces in
  // _may_delete_message, so the icon appears exactly where the request would
  // succeed. This is presentation only: the server re-decides every time, and
  // hiding the icon is never what stops an unauthorized delete.
  const canDelete = (m) => {
    if (!api.deleteMessage || m.optimistic) return false;
    if (role === "admin") return true;              // SuperAdmin runs the inbox
    if (locked) return false;                        // a finished transcript is fixed
    return Boolean(m.senderId) && m.senderId === meId;
  };

  const load = useCallback(async () => {
    try {
      const data = await api.listMessages(ticket.id);
      setMessages(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [api, ticket.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Live polling — this is what makes replies appear "live" on both sides.
  useEffect(() => {
    if (locked || !pollMs) return undefined;
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [load, locked, pollMs]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, loading]);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending || locked) return;
    if (body.length > 4000) {
      setError("Message too long (max 4000).");
      return;
    }
    setSending(true);
    setError(null);
    const optimistic = {
      id: `temp-${Date.now()}`,
      senderRole: role,
      senderId: meId,
      body,
      createdAt: new Date().toISOString(),
      optimistic: true,
    };
    setMessages((m) => [...m, optimistic]);
    setDraft("");
    try {
      const saved = await api.postMessage(ticket.id, body);
      setMessages((m) => m.map((x) => (x.id === optimistic.id ? saved : x)));
      onTicketUpdated?.();
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setDraft(body);
      setError(e?.status === 429 ? (e.message || "Slow down a moment.") : (e.message || "Send failed."));
    } finally {
      setSending(false);
    }
  };

  // Not optimistic, on purpose. A message removed from the list before the
  // server agreed would reappear on the next 7-second poll, which reads as the
  // delete having silently failed — so the row stays until the DELETE returns.
  const remove = async (m) => {
    if (deletingId || !canDelete(m)) return;
    setDeletingId(m.id);
    setError(null);
    try {
      await api.deleteMessage(ticket.id, m.id);
      setMessages((list) => list.filter((x) => x.id !== m.id));
      onTicketUpdated?.();
    } catch (e) {
      // The message is still on the server, so it stays on screen too.
      setError(e?.message || "Couldn't delete that message.");
    } finally {
      setDeletingId(null);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const placeholder = locked
    ? "This ticket is closed"
    : role === "admin"
      ? "Reply to the customer…"
      : "Message support…";

  return (
    // min-w-0 so this panel can shrink inside whatever flex/grid parent mounts
    // it, instead of forcing its column to the width of its longest message.
    <div data-probe="chat" className="flex min-w-0 max-w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 dark:border-white/10 dark:bg-white/5">
      <div className="flex items-center justify-between border-b border-slate-200 px-3.5 py-2 text-[0.6875rem] font-medium text-slate-500 dark:border-white/10 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <MessageSquare size={12} /> Live conversation
        </span>
        <span>{messages.length} message{messages.length === 1 ? "" : "s"}</span>
      </div>

      {/* The conversation scrolls on its own — overflow-x-hidden is the last
          line of defence, not the fix: the wrapping above is what keeps content
          inside, and nothing here is clipped because every bubble already fits. */}
      <div ref={scrollRef} className={cn("flex min-w-0 flex-col gap-2.5 overflow-y-auto overflow-x-hidden p-3.5", heightClass)}>
        {loading ? (
          <div className="grid flex-1 place-items-center py-8 text-xs text-slate-400">
            <Loader2 className="animate-spin" size={18} />
          </div>
        ) : messages.length === 0 ? (
          <div className="grid flex-1 place-items-center px-6 py-8 text-center text-xs text-slate-400">
            {role === "admin"
              ? "No messages yet — send the first reply."
              : "Support will reply right here. Feel free to add more details."}
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {messages.map((m) => {
              const mine = m.senderRole === role;
              const label = m.senderRole === role ? "You" : m.senderRole === "admin" ? "Support" : "You";
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ type: "spring", stiffness: 420, damping: 32 }}
                  // min-w-0 on the row: a flex child defaults to min-width:auto
                  // and refuses to shrink below its content, so an unbroken
                  // 800-character token in a bubble widened this row, the row
                  // widened the conversation, and the conversation widened the
                  // grid column the ticket list shares — which is how one long
                  // reply made the ticket list unusable.
                  data-probe="msg"
                  className={cn("group flex min-w-0 items-end gap-1", mine ? "justify-end" : "justify-start")}
                >
                  {/* The control sits OUTSIDE the bubble, before it on the
                      sender's side, so it can never overlap the text however
                      short the message is. */}
                  {mine && canDelete(m) && (
                    <DeleteButton m={m} busy={deletingId === m.id} onClick={() => remove(m)} />
                  )}
                  <div
                    data-probe="bubble"
                    className={cn(
                      // whitespace-pre-wrap keeps the sender's line breaks AND
                      // their runs of spaces (pasted, formatted text).
                      // overflow-wrap:anywhere is what break-words alone cannot
                      // do: it stops an unbreakable token inflating this
                      // bubble's min-content width, which is what actually
                      // keeps it inside the conversation.
                      "min-w-0 max-w-[80%] rounded-2xl px-3.5 py-2 text-[0.8125rem] leading-relaxed whitespace-pre-wrap break-words [overflow-wrap:anywhere] shadow-sm",
                      mine
                        ? "bg-gradient-to-br from-brand-600 to-violet-600 text-white rounded-br-md"
                        : "bg-white text-slate-700 ring-1 ring-slate-200 rounded-bl-md dark:bg-ink-800 dark:text-slate-200 dark:ring-white/10",
                      m.optimistic && "opacity-70"
                    )}
                  >
                    {m.body}
                    <div className={cn("mt-1 flex items-center gap-1 text-[0.625rem]",
                      mine ? "justify-end text-white/70" : "text-slate-400")}>
                      <span>{label} · {fmtRelative(m.createdAt)}</span>
                      {/* Delivery state, and ONLY for messages this side sent.
                          `m.optimistic` is the message that has not come back
                          from the API yet, so it gets no tick at all — the tick
                          means the server stored it, never that Send was
                          clicked. On a failed send the optimistic row is
                          removed entirely, so no tick can survive a failure.
                          Two ticks reuse the EXISTING read_at column the
                          backend already stamps when the other side opens the
                          thread; no new status concept is introduced. */}
                      {mine && !m.optimistic && (
                        m.readAt ? (
                          <CheckCheck size={12} aria-hidden="true" className="shrink-0" />
                        ) : (
                          <Check size={12} aria-hidden="true" className="shrink-0" />
                        )
                      )}
                      {mine && !m.optimistic && (
                        <span className="sr-only">{m.readAt ? "Sent and read" : "Sent"}</span>
                      )}
                    </div>
                  </div>
                  {!mine && canDelete(m) && (
                    <DeleteButton m={m} busy={deletingId === m.id} onClick={() => remove(m)} />
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      {error && (
        <div className="border-t border-rose-200/60 bg-rose-50 px-3.5 py-1.5 text-[0.6875rem] text-rose-600 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
          {error}
        </div>
      )}

      {!locked && (
        <div className="flex items-end gap-2 border-t border-slate-200 bg-white/70 p-2.5 dark:border-white/10 dark:bg-white/5">
          <textarea
            rows={1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder={placeholder}
            disabled={sending}
            className="max-h-24 flex-1 resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-[0.8125rem] text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-brand-400 dark:border-white/10 dark:bg-ink-900 dark:text-slate-100"
          />
          <button
            type="button"
            onClick={send}
            disabled={sending || !draft.trim()}
            aria-label="Send"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-600 to-violet-600 text-white shadow-soft transition enabled:hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
          </button>
        </div>
      )}
    </div>
  );
}
